import { Eye, Database } from "lucide-react";
import { useSelector } from "react-redux";
import { useState } from "react";
import DashboardLayout from "../app-layout";
import PageHeader from "@/components/fragments/page-header";
import { StatusBadge } from "@/components/fragments/status-badge";
import type { PurchaseOrder } from "@/types/purchaseOrder";
import { ReusableTable, type TableColumn } from "@/components/table";
import { PageContainer } from "@/components";
import { useGetPurchaseOrdersQuery } from "@/redux/services/purchaseOrder/purchaseOrderApi";
import { selectUser } from "@/redux/auth/authSlice";
import PurchaseOrderDetailsModal from "@/components/modals/purchase-order-details-modal";
import MobileFilterBar from "@/components/mobile/mobile-filter-bar";
import MobilePurchaseOrderList from "@/components/mobile/mobile-purchase-order-list";
import MobilePagination from "@/components/mobile/mobile-pagination";
import MobilePurchaseOrderModal from "@/components/mobile/mobile-purchase-order-modal";
import MobileNotificationBanner from "@/components/mobile/mobile-notification-banner";
import MobileBottomNav from "@/components/mobile/mobile-bottom-nav";

export default function PurchaseOrderPage() {
  // Get vendor ID from Redux store
  const user = useSelector(selectUser);
  const vendorId = user?.id;

  // Modal state
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Mobile modal state
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showNotification, setShowNotification] = useState(true);
  const [mobileStatusFilter, setMobileStatusFilter] = useState<
    "all" | "pending" | "accepted" | "declined"
  >("all");
  const [mobileSearchTerm, setMobileSearchTerm] = useState("");

  // Fetch purchase orders using the API
  const {
    data: purchaseOrdersResponse,
    isLoading,
    error,
    refetch: refetchPurchaseOrders,
  } = useGetPurchaseOrdersQuery(vendorId!, {
    skip: !vendorId, // Skip the query if vendorId is not available
  });

  // Extract purchase orders from API response
  const purchaseOrders: PurchaseOrder[] = purchaseOrdersResponse?.data || [];

  const mobileFilteredOrders = purchaseOrders.filter((order) => {
    const statusMatch =
      mobileStatusFilter === "all" || order.status === mobileStatusFilter;

    const search = mobileSearchTerm.trim().toLowerCase();
    const searchMatch =
      search.length === 0 ||
      order.poNumber?.toLowerCase().includes(search) ||
      order.buyersName?.toLowerCase().includes(search);

    return statusMatch && searchMatch;
  });

  const handleViewOrder = (order: PurchaseOrder) => {
    setSelectedOrderId(order.id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderId(null);
  };

  // Mobile handlers
  const handleMobileItemClick = (_item: any) => {
    setIsMobileModalOpen(true);
  };

  const handleMobileModalClose = () => {
    setIsMobileModalOpen(false);
  };

  const handleAcceptOrder = () => {
    // Handled in modal
  };

  const handleRejectOrder = () => {
    // Handled in modal
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "pending":
        return "warning";
      case "accepted":
        return "success";
      case "declined":
        return "danger";
      default:
        return "default";
    }
  };

  const getCustomStatusColors = (status: string) => {
    switch (status) {
      case "pending":
        return {
          background: "#FEF3C7",
          text: "#92400E",
          dot: "#F59E0B",
          border: "#FCD34D",
        };
      case "accepted":
        return {
          background: "#D1FAE5",
          text: "#065F46",
          dot: "#10B981",
          border: "#6EE7B7",
        };
      case "declined":
        return {
          background: "#FEE2E2",
          text: "#991B1B",
          dot: "#EF4444",
          border: "#FCA5A5",
        };
      default:
        return undefined;
    }
  };

  // Define table columns
  const columns: TableColumn<PurchaseOrder>[] = [
    {
      key: "poNumber",
      header: "PO Number",
      accessor: "poNumber",
      sortable: false,
      width: "120px",
      render: (value) => (
        <div className="flex items-center gap-2">
          <input type="checkbox" className="rounded" />
          <span className="font-medium text-primary">{value}</span>
        </div>
      ),
    },

    {
      key: "dateReceived",
      header: "DATE RECEIVED",
      accessor: "dateReceived",
      sortable: false,
      width: "150px",
      render: (value) => (
        <div className="flex items-center gap-5 justify-start">
          <h5 className="text-sm text-gray-600 DM-Sans font-[300]">{value}</h5>
        </div>
      ),
    },
    {
      key: "totalAmount",
      header: "TOTAL AMOUNT",
      accessor: "totalAmount",
      sortable: false,
      width: "150px",
      render: (value) => (
        <div className="flex items-center gap-5 justify-start">
          <h5 className="text-sm text-gray-900 DM-Sans font-[400]">{value}</h5>
        </div>
      ),
    },
    {
      key: "status",
      header: "STATUS",
      accessor: "status",
      sortable: false,
      width: "150px",
      render: (value) => (
        <StatusBadge
          status={value}
          variant={getStatusVariant(value)}
          customColors={getCustomStatusColors(value)}
          showDot={true}
          className="DM-Sans font-[300] border-0"
        />
      ),
    },
    {
      key: "action",
      header: "ACTION",
      accessor: "id",
      sortable: false,
      width: "150px",
      render: (_, order) => (
        <div className="flex items-center gap-10 lg:gap-24">
          <div
            className="flex items-center gap-1 text-gray-500 cursor-pointer hover:text-gray-700"
            onClick={() => handleViewOrder(order)}
          >
            <Eye className="h-4 w-4" />
            <span className="text-sm">View</span>
          </div>
        </div>
      ),
    },
  ];

  // No table actions needed since we're using inline view button
  const actions: any[] = [];

  // Show error state if there's an error and no vendor ID
  if (!vendorId) {
    return (
      <DashboardLayout>
        <section className="w-full h-full overflow-y-auto space-y-2 hide-scrollbar">
          <PageHeader
            title="Purchase Order"
            mobileTitle="Purchase Order"
            description="Listed below are list of your purchase order assigned to you"
            mobileDescription="Your purchase orders"
          />
          <PageContainer className="">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  No Vendor ID Found
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  Please ensure you are logged in with a valid vendor account.
                </p>
                <div className="mt-4 text-xs text-gray-400">
                  <p>Debug Info:</p>
                  <p>User exists: {user ? "Yes" : "No"}</p>
                  <p>User ID: {user?.id || "Not found"}</p>
                  <p>User object: {JSON.stringify(user, null, 2)}</p>
                </div>
              </div>
            </div>
          </PageContainer>
        </section>
      </DashboardLayout>
    );
  }

  // Show error state if there's an API error
  if (error && !isLoading) {
    return (
      <DashboardLayout>
        <section className="w-full h-full overflow-y-auto space-y-2 hide-scrollbar">
          <PageHeader
            title="Purchase Order"
            mobileTitle="Purchase Order"
            description="Listed below are list of your purchase order assigned to you"
            mobileDescription="Your purchase orders"
          />
          <PageContainer className="">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-red-600">
                  Error Loading Purchase Orders
                </h3>
                <p className="text-sm text-gray-500 mt-2">
                  There was an error fetching your purchase orders. Please try
                  again later.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          </PageContainer>
        </section>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <section className="w-full h-full overflow-y-auto space-y-2 hide-scrollbar">
        <PageHeader
          title="Purchase Order"
          mobileTitle="Purchase Order"
          description="Listed below are list of your purchase order assigned to you"
          mobileDescription="Listed below are list of your purchase order assigned to you"
        />

        {/* Desktop Layout */}
        <div className="hidden md:block">
          <PageContainer className="">
            <ReusableTable
              title=""
              data={purchaseOrders}
              columns={columns}
              actions={actions}
              loading={isLoading}
              searchable={true}
              searchPlaceholder="Search for product..."
              className="DM-Sans"
              filters={[
                {
                  key: "status",
                  label: "Status",
                  options: [
                    { label: "Pending", value: "pending" },
                    { label: "Accepted", value: "accepted" },
                    { label: "Declined", value: "declined" },
                  ],
                },
              ]}
              pagination={true}
              pageSize={20}
              height="600px"
              emptyStateTitle="No purchase orders found"
              emptyStateDescription="No purchase orders match your current search and filter criteria."
            />
          </PageContainer>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden pb-20">
          {/* Notification Banner */}
          {showNotification && (
            <MobileNotificationBanner
              message="A new Purchase Order has been issued to you. Please review and confirm."
              onDismiss={() => setShowNotification(false)}
            />
          )}

          {/* Filter Bar */}
          <MobileFilterBar
            statusValue={mobileStatusFilter}
            onStatusChange={(status) => {
              setMobileStatusFilter(status);
              setCurrentPage(1);
            }}
            onSearchChange={(term) => {
              setMobileSearchTerm(term);
              setCurrentPage(1);
            }}
          />

          {/* Purchase Order List */}
          {mobileFilteredOrders.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center p-8 text-center min-h-[300px]">
              <Database className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                No purchase orders found
              </h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-sm">
                No purchase orders match your current search and filter
                criteria.
              </p>
            </div>
          ) : (
            <>
              <div className="px-4 py-4">
                <MobilePurchaseOrderList
                  items={mobileFilteredOrders}
                  onItemClick={handleMobileItemClick}
                  onViewDetails={(item) => {
                    setSelectedOrderId(item.id);
                    setIsMobileModalOpen(true);
                  }}
                />
              </div>

              {/* Pagination - Only show if there's data */}
              {mobileFilteredOrders.length > 0 && (
                <MobilePagination
                  currentPage={currentPage}
                  totalPages={Math.ceil(mobileFilteredOrders.length / 20) || 1}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      </section>

      {/* Purchase Order PDF Modal */}
      <PurchaseOrderDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        purchaseOrderId={selectedOrderId}
        onStatusChange={() => {
          refetchPurchaseOrders();
        }}
      />

      {/* Mobile Purchase Order Modal */}
      <MobilePurchaseOrderModal
        isOpen={isMobileModalOpen}
        onClose={handleMobileModalClose}
        purchaseOrderId={selectedOrderId}
        onAcceptOrder={handleAcceptOrder}
        onRejectOrder={handleRejectOrder}
        onStatusChange={() => {
          refetchPurchaseOrders();
        }}
      />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </DashboardLayout>
  );
}
